import React, { useRef, useState } from 'react';

interface DocumentsUploadSectionProps {
  documents: {
    identity: File | null;
    authorization: File | null;
    businessRegistry: File | null;
    representativeDocument: File | null;
  };
  errors: Record<string, string>;
  onFileChange: (field: string, file: File | null) => void;
  existingDocuments?: {
    identity: { filename: string; originalName: string } | null;
    authorization: { filename: string; originalName: string } | null;
    businessRegistry: { filename: string; originalName: string } | null;
    representativeDocument: { filename: string; originalName: string } | null;
  };
  editToken?: string;
}

const DocumentsUploadSection: React.FC<DocumentsUploadSectionProps> = ({
  documents,
  errors,
  onFileChange,
  existingDocuments,
  editToken
}) => {
  const identityRef = useRef<HTMLInputElement | null>(null);
  const authorizationRef = useRef<HTMLInputElement | null>(null);
  const businessRegistryRef = useRef<HTMLInputElement | null>(null);
  const representativeDocumentRef = useRef<HTMLInputElement | null>(null);

  // Stato per tracciare i file in caricamento
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});

  const handleDownload = (documentType: string) => {
    if (!editToken) return;

    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/memberships/download/${editToken}/${documentType}`;
    window.open(url, '_blank');
  };

  const handleFileSelect = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      // Verifica che sia un PDF
      if (file.type !== 'application/pdf') {
        alert('Solo file PDF sono ammessi');
        event.target.value = '';
        return;
      }

      // Verifica dimensione (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Il file è troppo grande. Dimensione massima: 10MB');
        event.target.value = '';
        return;
      }

      // Mostra indicatore di caricamento IMMEDIATAMENTE
      setLoadingFiles(prev => ({ ...prev, [field]: true }));

      // Usa requestAnimationFrame per garantire che lo spinner venga renderizzato
      // prima di chiamare onFileChange che potrebbe bloccare il thread
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onFileChange(field, file);

          // Nascondi indicatore di caricamento dopo un breve momento
          setTimeout(() => {
            setLoadingFiles(prev => ({ ...prev, [field]: false }));
          }, 500);
        });
      });
    } else {
      // Se il file è null (rimosso), aggiorna immediatamente
      onFileChange(field, null);
    }
  };

  const renderFileInput = (
    label: string,
    field: string,
    file: File | null,
    ref: React.RefObject<HTMLInputElement | null>,
    required: boolean = false
  ) => {
    const existingDoc = existingDocuments?.[field as keyof typeof existingDocuments];
    const hasExistingDoc = existingDoc && !file;

    return (
      <div className="flex flex-col">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label} {required && '*'}
        </label>

        {/* Mostra documento esistente se presente e non c'è un nuovo file */}
        {hasExistingDoc && (
          <div className="mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                    {existingDoc.originalName}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    File esistente - Verrà mantenuto se non carichi un nuovo file
                  </p>
                </div>
              </div>
              {editToken && (
                <button
                  type="button"
                  onClick={() => handleDownload(field)}
                  className="ml-2 p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors flex-shrink-0"
                  title="Scarica documento"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <input
            ref={ref}
            type="file"
            accept=".pdf"
            onChange={(e) => handleFileSelect(field, e)}
            className="hidden"
          />
          
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={loadingFiles[field]}
            className="flex-1 px-4 py-3 md:py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors text-left touch-manipulation active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              {loadingFiles[field] ? (
                // Spinner di caricamento
                <svg className="w-6 h-6 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )}
              <div className="flex-1 min-w-0">
                {loadingFiles[field] ? (
                  <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Caricamento in corso...
                  </p>
                ) : file ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Clicca per selezionare un file PDF
                  </p>
                )}
              </div>
            </div>
          </button>
          
          {file && (
            <button
              type="button"
              onClick={() => {
                onFileChange(field, null);
                if (ref.current) ref.current.value = '';
              }}
              className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Rimuovi file"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
        
        {errors[field] && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[field]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-black/5 dark:ring-white/10 p-6 sm:p-8">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Documenti Allegati
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Tutti i documenti devono essere in formato PDF. Dimensione massima per file: 10MB
      </p>

      <div className="space-y-6">
        {renderFileInput(
          'Documento di identità del firmatario',
          'identity',
          documents.identity,
          identityRef,
          true
        )}

        {renderFileInput(
          'Autorizzazione',
          'authorization',
          documents.authorization,
          authorizationRef,
          true
        )}

        {renderFileInput(
          'Visura camerale o autocertificazione',
          'businessRegistry',
          documents.businessRegistry,
          businessRegistryRef,
          true
        )}

        {renderFileInput(
          'Documento di rappresentatività (opzionale)',
          'representativeDocument',
          documents.representativeDocument,
          representativeDocumentRef,
          false
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Documenti richiesti:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Documento di identità valido del firmatario della domanda</li>
              <li>Autorizzazione firmata</li>
              <li>Visura camerale aggiornata o autocertificazione</li>
              <li>Documento di rappresentatività (se applicabile)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsUploadSection;

